<template>
    <div class="c-nongye-left">
        <top-tabs :tabsObj="tabsObj"></top-tabs>
        <div class="c-nongye-left__content">
            <div class="c-nongye-left__search">
                <el-input v-model="keyword" :placeholder="$t('nongye.left.searchPlaceholder')" clearable size="small" prefix-icon="el-icon-search" @input="handleSearch"></el-input>
            </div>
            <YScroll class="c-nongye-left__body">
                <el-tree
                    ref="treeRef"
                    class="c-nongye-left__tree"
                    node-key="id"
                    :indent="12"
                    :data="treeData"
                    :props="treeProps"
                    highlight-current
                    default-expand-all
                    :expand-on-click-node="false"
                    :filter-node-method="filterNode"
                    @node-click="handleNodeClick"
                    @node-expand="handleNodeExpand"
                    @node-collapse="handleNodeCollapse"
                >
                    <template #default="{ data }">
                        <div v-if="isPlot(data)" class="leaf-node" :class="{ active: activeId === data.id }">
                            <div class="leaf-row leaf-title">
                                <span class="leaf-name">{{ data.plotName }}</span>
                                <span class="leaf-area">{{ data.area }} {{ $t('nongye.left.mu') }}</span>
                            </div>
                            <el-tooltip class="leaf-row leaf-address" effect="dark" :content="$t('nongye.left.address', { address: data.address || '---' })" placement="top" :show-after="200" :disabled="!(data.address && data.address.length > 20)">
                                <div class="leaf-address-text">{{ $t('nongye.left.addressLabel') }}{{ data.address || '---' }}</div>
                            </el-tooltip>
                        </div>
                        <div v-else class="branch-node" :class="{ active: activeBranchId === data.id, loading: loadingAreaId === data.id }" @click.stop="handleBranchClick(data)">
                            <span class="branch-label">
                                {{ data.name }}<span class="branch-count" v-if="getBranchCount(data) != null"> {{ getBranchCount(data) }}</span>
                            </span>
                            <i v-if="loadingAreaId === data.id" class="el-icon-loading branch-toggle"></i>
                            <i v-else class="el-icon-arrow-down branch-toggle" :class="{ expanded: isNodeExpanded(data) }" @click.stop="handleToggleClick(data)"></i>
                        </div>
                    </template>
                </el-tree>
            </YScroll>
        </div>
    </div>
</template>

<script>
import { YScroll } from '@CommonComponent';
import TopTabs from '@/components/topTabs.vue';
import NongyeApi from '../../api';

export default {
    name: 'LeftAside',
    components: { TopTabs, YScroll },
    props: {
        treeData: { type: Array, default: () => [] },
        activeId: { type: [String, Number], default: null },
    },
    data() {
        return {
            keyword: '',
            tabsObj: { centre: this.$t('nongye.left.plotList'), type: 3 },
            activeBranchId: null,
            loadingAreaId: null, // 当前正在懒加载的区域节点 id
        };
    },
    computed: {
        treeProps() {
            return {
                label: 'name',
                children: 'children',
            };
        },
    },
    watch: {
        treeData: {
            deep: true,
            handler() {
                this.$nextTick(() => {
                    if (this.$refs.treeRef) {
                        this.$refs.treeRef.filter(this.keyword);
                        this.initExpandedFlag(this.treeData);
                        // 等 el-tree 完成 default-expand-all 后强制刷新图标
                        this.$nextTick(() => this.$forceUpdate());
                    }
                });
            },
        },
    },
    mounted() {
        // 兼容 treeData 在 mounted 之前已传入的场景：通知父组件做懒加载
        this.$nextTick(() => this.requestAutoLoad(this.treeData));
    },
    methods: {
        isPlot(data) {
            return data && (data.area !== undefined || data.landType !== undefined);
        },
        initExpandedFlag(nodes) {
            const walk = (list) => {
                if (!Array.isArray(list)) return;
                list.forEach((n) => {
                    if (n.children) walk(n.children);
                });
            };
            walk(nodes);
            // 通过 emit 通知父组件去懒加载，避免在子组件内修改 prop treeData
            this.$nextTick(() => this.requestAutoLoad(nodes));
        },
        // 通知父组件：这些 area 节点需要加载 children
        requestAutoLoad(nodes) {
            if (!Array.isArray(nodes)) return;
            const areaIds = [];
            const walk = (list) => {
                if (!Array.isArray(list)) return;
                list.forEach((n) => {
                    if (n && n.nodeType === 'area' && !Array.isArray(n.children)) {
                        areaIds.push(n.id);
                    }
                    if (n.children) walk(n.children);
                });
            };
            walk(nodes);
            if (areaIds.length) this.$emit('load-children', areaIds);
        },
        // 直接读取 el-tree 的真实展开状态，避免与 default-expand-all 不一致
        isNodeExpanded(data) {
            if (!data || !this.$refs.treeRef) return false;
            const node = this.$refs.treeRef.getNode(data);
            return node ? Boolean(node.expanded) : false;
        },
        getBranchCount(data) {
            if (data == null) return null;
            if (data.count != null) return data.count;
            const countLeaves = (nodes) => {
                let total = 0;
                if (!Array.isArray(nodes)) return total;
                nodes.forEach((n) => {
                    if (this.isPlot(n)) {
                        total += 1;
                    } else if (n.children) {
                        total += countLeaves(n.children);
                    }
                });
                return total;
            };
            if (Array.isArray(data.children) && data.children.length > 0) {
                const total = countLeaves(data.children);
                if (total > 0) return total;
                return data.children.length;
            }
            return null;
        },
        toggleExpand(data) {
            const node = this.$refs.treeRef.getNode(data);
            if (!node) return;
            if (node.expanded) {
                node.collapse();
            } else {
                node.expand();
            }
        },
        // 点击区域行：选中当前区域并通知父组件刷新统计（不展开/收起，展开由箭头负责）
        handleBranchClick(data) {
            this.activeBranchId = data.id;
            // 如果是 area 节点且没有 children，触发懒加载
            if (data && data.nodeType === 'area' && !Array.isArray(data.children)) {
                this.$emit('load-children', [data.id]);
            }
            this.$emit('node-click', data);
        },
        // 点击箭头：只负责展开/收起
        handleToggleClick(data) {
            this.toggleExpand(data);
            // 强制刷新，让 isNodeExpanded 重新求值
            this.$nextTick(() => this.$forceUpdate());
        },
        handleNodeExpand(data) {
            // 强制刷新以触发 branch-toggle 的 class 重算
            this.$forceUpdate();
        },
        handleNodeCollapse(data) {
            this.$forceUpdate();
        },
        filterNode(value, data) {
            if (!this.isPlot(data)) return true;
            const kw = (this.keyword || '').trim();
            if (!kw) return true;
            const label = `${data.plotName || data.name || ''} ${data.plotCode || ''}`;
            return String(label).includes(kw);
        },
        doFilter() {
            this.$refs.treeRef && this.$refs.treeRef.filter(this.keyword);
        },
        handleSearch() {
            this.doFilter();
            this.$emit('search', { keyword: this.keyword, startDate: '', endDate: '' });
        },
        handleNodeClick(data) {
            if (this.isPlot(data)) {
                this.$emit('node-click', data);
                this.$emit('select', data);
            }
        },
        async handleDelete(data) {
            try {
                await this.$confirm(this.$t('nongye.left.confirmDeletePlot', { name: data.plotName }), this.$t('nongye.left.tip'), { type: 'warning' });
                await NongyeApi.delAgriPlot(data.id);
                this.$message.success(this.$t('nongye.left.deleteSuccess'));
                this.$emit('delete', data);
            } catch (e) {
                if (e !== 'cancel') this.$message.error(this.$t('nongye.left.deleteFail'));
            }
        },
        handleEdit(data) {
            this.$emit('edit', data);
        },
    },
};
</script>

<style lang="scss" scoped>
.c-nongye-left {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    color: #fff;

    &__content {
        flex: 1;
        display: flex;
        flex-direction: column;
        padding: 8px 12px;
        min-height: 0;
    }

    &__search {
        margin-bottom: 10px;
        padding-top: 4px;

        ::v-deep .el-input__inner {
            background: rgba(10, 26, 58, 0.6);
            border-color: #1a47a4;
            color: #fff;
        }

        ::v-deep .el-input__inner::placeholder {
            color: #9bb0d9;
        }
    }

    &__body {
        flex: 1;
        min-height: 0;
    }

    &__tree {
        background: transparent;
        color: #fff;
        padding: 0;

        // 只清零第一层（根节点）的 padding-left，子节点保留 el-tree 默认累加缩进
        ::v-deep > .el-tree-node {
            padding-left: 0;
        }

        ::v-deep .el-tree-node__content {
            height: auto;
            align-items: flex-start;
            //  padding-left: 8px !important;
            margin: 2px 0;
            background: transparent !important;
            border: none !important;
        }

        ::v-deep .el-tree-node.is-current > .el-tree-node__content {
            background: transparent !important;
        }

        // 自定义分支样式：隐藏默认展开箭头，使用自定义 branch-toggle
        ::v-deep .el-tree-node__expand-icon {
            display: none !important;
        }

        // 子级缩进：覆盖 el-tree 默认 18px，层级感更明显
        ::v-deep .el-tree-node__children {
            padding-left: 0px;
        }

        ::v-deep .leaf-node {
            width: 100%;
            padding: 5px 8px 5px 8px;
            // 设计稿：更亮的蓝色渐变 + 1px 蓝色描边 + 左侧 3px 蓝青竖条
            background: linear-gradient(90deg, rgba(10, 167, 255, 0.35) 0%, rgba(10, 167, 255, 0.12) 50%, rgba(10, 167, 255, 0) 100%);
            border: 1px solid rgba(10, 167, 255, 0.55);
            border-left: 3px solid #0aa7ff;
            border-radius: 2px;
            box-sizing: border-box;
            margin: 0;
            cursor: pointer;
            transition: all 0.2s;

            &.active {
                background: linear-gradient(90deg, rgba(10, 167, 255, 0.5) 0%, rgba(10, 167, 255, 0.2) 50%, rgba(10, 167, 255, 0.05) 100%);
                border-color: #5bf0fc;
                border-left-color: #5bf0fc;
            }

            &:hover {
                background: linear-gradient(90deg, rgba(10, 167, 255, 0.45) 0%, rgba(10, 167, 255, 0.18) 50%, rgba(10, 167, 255, 0.04) 100%);
            }

            .leaf-row {
                line-height: 18px;
            }

            .leaf-title {
                display: flex;
                justify-content: space-between;
                align-items: center;
                font-size: 13px;
                color: #fff;
                width: 100%;

                .leaf-name {
                    font-weight: 500;
                    flex: 1;
                    min-width: 0;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    padding-right: 8px;
                }

                .leaf-area {
                    // color: #9bb0d9;
                    flex-shrink: 0;
                    white-space: nowrap;
                }
            }

            .leaf-address {
                font-size: 12px;
                // color: #8fa4cd;
                margin-top: 3px;
                display: block !important;
                width: 100% !important;
                max-width: 100% !important;
                min-width: 0;
            }

            .leaf-address-text {
                display: block !important;
                width: 100% !important;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                box-sizing: border-box;
            }
        }

        ::v-deep .branch-node {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            min-height: 32px;
            font-size: 14px;
            color: #5bf0fc;
            line-height: 32px;
            padding: 0 10px 0 8px;
            margin: 2px 0;
            cursor: pointer;
            // 设计稿：未级行为透明 + 浅蓝描边，类似叶子但更"空"——分级感更强
            background: transparent;
            border: 1px solid rgba(10, 167, 255, 0.45);
            border-left: 3px solid rgba(10, 167, 255, 0.7);
            border-radius: 2px;
            box-sizing: border-box;
            transition: all 0.2s;

            &:hover {
                background: linear-gradient(90deg, rgba(10, 167, 255, 0.18) 0%, rgba(10, 167, 255, 0.05) 100%);
                border-color: rgba(10, 167, 255, 0.7);
            }

            &.active {
                background: linear-gradient(90deg, rgba(10, 167, 255, 0.3) 0%, rgba(10, 167, 255, 0.08) 100%);
                border-color: #5bf0fc;
                border-left-color: #5bf0fc;
                // 设计稿 active 状态：去掉红色 inset
                box-shadow: none;
            }

            &.loading {
                opacity: 0.7;
                cursor: wait;
            }

            .branch-label {
                flex: 1;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                font-weight: 500;
            }

            .branch-count {
                color: #ffffff;
                font-size: 13px;
                font-weight: 600;
            }

            .branch-toggle {
                font-size: 14px;
                color: #5bf0fc;
                transition: transform 0.2s;
                flex-shrink: 0;
                margin-left: 8px;

                &.expanded {
                    transform: rotate(180deg);
                }
            }
        }
    }
}
</style>
